import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Banknote, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Carrier-only nudge about Cash-on-Delivery outstanding balance.
 *
 * The *primary* settlement path is always automatic: outstanding commission is
 * deducted from the carrier's next online-payment payout. This banner only
 * exists so the carrier knows:
 *   (a) they currently owe something, and
 *   (b) completing one online-payment delivery will clear it.
 *
 * Three states:
 *   • blocked  → red: "COD paused — do one online delivery to clear this"
 *   • warning  → amber: "₹X of ₹Y limit used — next online delivery clears it"
 *   • idle     → nothing rendered (no debt)
 */
const CodNudgeBanner = ({ onGoFindParcels }) => {
  const [cod, setCod] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const r = await api.get("/users/me/earnings");
        if (!cancel) setCod(r.data?.cod || null);
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60_000); // refresh once a minute
    return () => { cancel = true; clearInterval(iv); };
  }, []);

  if (!cod || !(cod.cod_balance_owed > 0)) return null;

  const owed = Number(cod.cod_balance_owed || 0);
  const ceiling = Number(cod.cod_max_outstanding || 0);
  const overLimit = ceiling > 0 && owed > ceiling;
  const highUse = ceiling > 0 && !overLimit && owed / ceiling >= 0.8;
  if (!overLimit && !highUse) return null;

  const palette = overLimit
    ? {
        wrap: "bg-rose-50 border-rose-200",
        icon: "text-rose-700",
        title: "text-rose-900",
        body: "text-rose-800",
        pill: "bg-rose-100 text-rose-900",
        btn:  "bg-rose-700 hover:bg-rose-800 text-white",
      }
    : {
        wrap: "bg-amber-50 border-amber-200",
        icon: "text-amber-700",
        title: "text-amber-900",
        body: "text-amber-900/90",
        pill: "bg-amber-100 text-amber-900",
        btn:  "bg-amber-700 hover:bg-amber-800 text-white",
      };

  return (
    <div
      data-testid={overLimit ? "cod-blocked-banner" : "cod-warning-banner"}
      className={`rounded-2xl border-2 p-4 mb-4 ${palette.wrap}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`shrink-0 ${palette.icon} mt-0.5`}>
          {overLimit ? <AlertTriangle className="w-5 h-5"/> : <Banknote className="w-5 h-5"/>}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className={`font-display font-bold ${palette.title}`}>
            {overLimit
              ? <>COD is paused — ₹{owed.toFixed(0)} commission owed</>
              : <>₹{owed.toFixed(0)} COD commission building up</>}
          </div>
          <div className={`text-xs mt-1 ${palette.body}`}>
            {overLimit
              ? <>You've gone over the ₹{ceiling.toFixed(0)} limit. <b>Complete one online-payment delivery</b> and we'll automatically deduct this amount from that payout — no manual action needed.</>
              : <>You're at ₹{owed.toFixed(0)} of your ₹{ceiling.toFixed(0)} COD limit. Your <b>next online-payment delivery</b> will auto-settle this balance from your earnings.</>}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-full text-[11px] font-medium px-2.5 py-1 ${palette.pill}`}>
              <Banknote className="w-3 h-3"/> Owed ₹{owed.toFixed(0)} · limit ₹{ceiling.toFixed(0)}
            </span>
            <button
              onClick={onGoFindParcels}
              data-testid="cod-nudge-find-parcels"
              className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-3 py-1.5 ${palette.btn}`}
            >
              Find online-payment parcels <ArrowRight className="w-3 h-3"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodNudgeBanner;
